'use client';

import { useSession } from '@repo/auth';
import {
  getMovieRatingV1Api,
  type IMovieRatingData,
  type IRateMovieV1Body,
  rateMovieV1Api,
} from '@repo/sdk';
import { toast } from '@repo/ui';
import { useCallback, useEffect, useState } from 'react';
import { applyTrackingUpdate } from './apply-tracking-update';
import { captureTrackingError } from './capture-tracking-error';

type TrackingStatusType = 'loading' | 'signedOut' | 'error' | 'ready';

interface ICollectionTrackingOptions {
  collectionId: number;
  saveErrorMessage: string;
}

interface ICollectionTracking {
  status: TrackingStatusType;
  data: IMovieRatingData;
  pending: boolean;
  save: (update: IRateMovieV1Body) => Promise<void>;
  retry: () => void;
}

const EMPTY_DATA: IMovieRatingData = {
  watched: false,
  watchCount: 0,
  wantsToWatch: false,
  rating: null,
};

function resolveTrackingStatus(
  sessionPending: boolean,
  signedIn: boolean,
  failed: boolean,
  loaded: boolean,
): TrackingStatusType {
  if (sessionPending) {
    return 'loading';
  }
  if (!signedIn) {
    return 'signedOut';
  }
  if (failed) {
    return 'error';
  }
  return loaded ? 'ready' : 'loading';
}

export function useCollectionTracking({
  collectionId,
  saveErrorMessage,
}: ICollectionTrackingOptions): ICollectionTracking {
  const { data: session, isPending: sessionPending } = useSession();
  const signedIn = Boolean(session?.user);
  const [data, setData] = useState<IMovieRatingData>(EMPTY_DATA);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [pending, setPending] = useState(false);
  const [fetchNonce, setFetchNonce] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchNonce re-runs the load on retry
  useEffect(() => {
    if (!signedIn) {
      return;
    }
    let active = true;
    setFailed(false);
    setLoaded(false);
    getMovieRatingV1Api({ collectionId })
      .then((res) => {
        if (!active) {
          return;
        }
        const payload = res.data.data;
        if (payload) {
          setData(payload);
        }
        setLoaded(true);
      })
      .catch((error) => {
        captureTrackingError(error, collectionId, 'GET /user-collections/:id/rating/v1');
        if (active) {
          setFailed(true);
        }
      });
    return () => {
      active = false;
    };
  }, [signedIn, collectionId, fetchNonce]);

  const save = useCallback(
    async (update: IRateMovieV1Body): Promise<void> => {
      if (pending) {
        return;
      }
      setPending(true);
      const snapshot = data;
      setData(applyTrackingUpdate(data, update));
      try {
        const res = await rateMovieV1Api({ collectionId, ...update });
        const payload = res.data.data;
        if (payload) {
          setData(payload);
        }
      } catch (error) {
        captureTrackingError(error, collectionId, 'PUT /user-collections/:id/rating/v1');
        setData(snapshot);
        toast.error(saveErrorMessage);
      } finally {
        setPending(false);
      }
    },
    [collectionId, data, pending, saveErrorMessage],
  );

  const retry = useCallback((): void => {
    setFetchNonce((nonce) => nonce + 1);
  }, []);

  const status = resolveTrackingStatus(sessionPending, signedIn, failed, loaded);
  return { status, data, pending, save, retry };
}
